import { isCvinExpansion, summaryGroup } from "../projectGridColumns";
import { displayPhaseForRow } from "../projectPhaseDisplay";
import { readCommercialDomainsForRow, repeaterToArray } from "./commercialProjectAcf";
import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import { normalizeMaterialRows, aggregateMaterialTotals } from "./engineeringMaterialsTotals";
import { displayNameForMaterial, unitLabelForMaterialName } from "./engineeringMaterialsCatalog";
import { readSubsegmentSummaryFromAcf } from "./expansionProjectAcf";
import { workOrderBaseForRow } from "./expansionGroupUtils";

const LEGACY_INVENTORY_KEYS = ["Inventory Test 2", "inventory_test_2"];

function parseQuantityNum(raw) {
  const s = String(raw || "").replace(/,/g, "").trim();
  if (!s || !/^\d+(\.\d+)?$/.test(s)) return null;
  return Number(s);
}

function projectTitle(row) {
  const sub = readSubsegmentSummaryFromAcf(row?.acf_groups_json);
  if (sub?.project_name) return String(sub.project_name).trim();
  const summary = summaryGroup(row);
  return String(summary?.project_name ?? row?.name ?? row?.site_name ?? row?.customer ?? "").trim();
}

function projectOwner(row) {
  const sub = readSubsegmentSummaryFromAcf(row?.acf_groups_json);
  if (sub?.owner) return String(sub.owner).trim();
  const summary = summaryGroup(row);
  return String(summary?.owner ?? row?.owner ?? "").trim();
}

function readLegacyInventoryMaterials(row) {
  const acf = row?.acf_groups_json;
  if (!acf || typeof acf !== "object") return [];
  for (const key of LEGACY_INVENTORY_KEYS) {
    const inv = acf[key];
    if (inv?.material_totals) return normalizeMaterialRows(inv.material_totals);
  }
  return [];
}

function pushMaterialLine(rows, base, mat, extra) {
  const item = String(mat?.item || "").trim();
  const slug = String(mat?.slug || "").trim();
  const quantity = String(mat?.quantity || "").trim();
  if (!item && !slug && !quantity) return;
  const displayItem = displayNameForMaterial(slug || item);
  if (!displayItem && !quantity) return;
  rows.push({
    ...base,
    materialSlug: slug || undefined,
    materialItem: displayItem,
    quantity,
    quantityNum: parseQuantityNum(quantity),
    unit: unitLabelForMaterialName(slug || displayItem),
    ...extra,
  });
}

/** Flatten permit and legacy inventory material lines across all projects. */
export function buildMaterialsInventoryRows(projects) {
  const rows = [];

  for (const project of Array.isArray(projects) ? projects : []) {
    const expansion = isCvinExpansion(project);
    const portfolio = expansion ? "expansion" : "commercial";
    const projectId = project.id;
    const workOrder = String(project.work_order ?? "").trim();
    const woBase = workOrderBaseForRow(project);
    const subSummary = readSubsegmentSummaryFromAcf(project?.acf_groups_json);
    const base = {
      portfolio,
      projectId,
      workOrder,
      woBase,
      segment: subSummary?.segment != null ? String(subSummary.segment).trim() : "",
      projectName: projectTitle(project),
      owner: projectOwner(project),
      phase: displayPhaseForRow(project).trim(),
    };

    const domains = readCommercialDomainsForRow(project, project.acf_domains);
    const permits = repeaterToArray(domains?.[COMMERCIAL_DOMAINS.ENGINEERING]?.permits);
    permits.forEach((permit, permitIndex) => {
      const permitBase = {
        ...base,
        source: "Permit",
        permitAgency: String(permit?.agency ?? "").trim(),
        permitNumber: String(permit?.permit_number ?? "").trim(),
        permitReceivedDate: String(permit?.permit_received_date ?? "").trim(),
        reviewPending: permit?.materials_review_pending === true,
      };
      normalizeMaterialRows(permit?.materials).forEach((mat, matIndex) => {
        pushMaterialLine(rows, base, mat, {
          ...permitBase,
          id: `${projectId}-permit-${permitIndex}-${matIndex}`,
        });
      });
    });

    readLegacyInventoryMaterials(project).forEach((mat, matIndex) => {
      pushMaterialLine(rows, base, mat, {
        id: `${projectId}-legacy-${matIndex}`,
        source: "Legacy inventory",
        permitAgency: "",
        permitNumber: "",
        permitReceivedDate: "",
        reviewPending: false,
      });
    });
  }

  return rows.sort((a, b) => {
    const wo = String(a.workOrder).localeCompare(String(b.workOrder), undefined, {
      numeric: true,
      sensitivity: "base",
    });
    if (wo !== 0) return wo;
    return String(a.materialItem).localeCompare(String(b.materialItem), undefined, {
      sensitivity: "base",
    });
  });
}

export function filterMaterialsInventoryRows(rows, filters = {}) {
  const { portfolio, phases = [], materialItem = "", search = "" } = filters;
  const phaseSet = new Set((phases || []).map((p) => String(p).trim()).filter(Boolean));
  const searchLower = String(search || "").trim().toLowerCase();
  const materialLower = String(materialItem || "").trim().toLowerCase();

  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (portfolio && row.portfolio !== portfolio) return false;
    if (phaseSet.size > 0 && !phaseSet.has(row.phase)) return false;
    if (materialLower && String(row.materialItem || "").trim().toLowerCase() !== materialLower) {
      return false;
    }
    if (searchLower) {
      const hay = [
        row.workOrder,
        row.woBase,
        row.projectName,
        row.materialItem,
        row.permitAgency,
        row.owner,
        row.segment,
        row.permitNumber,
      ]
        .join(" ")
        .toLowerCase();
      if (!hay.includes(searchLower)) return false;
    }
    return true;
  });
}

export function listMaterialItemOptions(rows) {
  const items = new Set();
  for (const row of Array.isArray(rows) ? rows : []) {
    const item = String(row.materialItem || "").trim();
    if (item) items.add(item);
  }
  return [...items].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

export function summarizeMaterialsInventory(rows) {
  const projectIds = new Set();
  const materials = new Set();
  let reviewPendingCount = 0;

  for (const row of Array.isArray(rows) ? rows : []) {
    projectIds.add(row.projectId);
    const slug = String(row.materialSlug || "").trim().toLowerCase();
    const item = String(row.materialItem || "").trim();
    if (slug) materials.add(slug);
    else if (item) materials.add(item.toLowerCase());
    if (row.reviewPending) reviewPendingCount += 1;
  }

  return {
    lineCount: rows.length,
    projectCount: projectIds.size,
    uniqueMaterials: materials.size,
    reviewPendingCount,
  };
}

/** Portfolio totals grouped by catalog material slug (name is a label). */
export function aggregateMaterialsInventoryByItem(rows) {
  const totals = aggregateMaterialTotals(
    (Array.isArray(rows) ? rows : []).map((row) => ({
      slug: row.materialSlug,
      item: row.materialItem,
      quantity: row.quantity,
    }))
  );

  const lineCounts = new Map();
  const projectSets = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const slug = String(row.materialSlug || "").trim().toLowerCase();
    const item = String(row.materialItem || "").trim();
    const key = slug || item.toLowerCase();
    if (!key) continue;
    lineCounts.set(key, (lineCounts.get(key) || 0) + 1);
    if (!projectSets.has(key)) projectSets.set(key, new Set());
    projectSets.get(key).add(row.projectId);
  }

  return totals.map((row) => {
    const key = String(row.slug || row.item || "")
      .trim()
      .toLowerCase();
    return {
      id: key || row.item,
      materialItem: row.item,
      quantity: row.quantity,
      quantityNum: parseQuantityNum(row.quantity),
      unit: unitLabelForMaterialName(row.slug || row.item),
      lineCount: lineCounts.get(key) || 0,
      projectCount: projectSets.get(key)?.size || 0,
    };
  });
}

/** One row per project for a filtered material, summing quantity across permit lines. */
export function aggregateMaterialsInventoryByProject(rows) {
  const byId = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const projectId = row.projectId;
    if (projectId == null || projectId === "") continue;
    const key = String(projectId);
    const qty = row.quantityNum;
    const prev = byId.get(key);
    if (!prev) {
      byId.set(key, {
        id: key,
        projectId,
        workOrder: row.workOrder || "",
        projectName: row.projectName || "",
        owner: row.owner || "",
        phase: row.phase || "",
        segment: row.segment || "",
        materialItem: row.materialItem || "",
        unit: row.unit || "",
        quantityNum: qty,
        quantity: qty != null ? String(qty) : row.quantity || "",
        lineCount: 1,
        reviewPendingCount: row.reviewPending ? 1 : 0,
      });
      continue;
    }
    prev.lineCount += 1;
    if (row.reviewPending) prev.reviewPendingCount += 1;
    if (prev.quantityNum != null && qty != null) {
      prev.quantityNum += qty;
      prev.quantity = String(prev.quantityNum);
    } else if (prev.quantityNum == null && qty != null && !prev.quantity) {
      prev.quantityNum = qty;
      prev.quantity = String(qty);
    }
    if (!prev.workOrder && row.workOrder) prev.workOrder = row.workOrder;
    if (!prev.projectName && row.projectName) prev.projectName = row.projectName;
  }

  return [...byId.values()].sort((a, b) => {
    const wo = String(a.workOrder).localeCompare(String(b.workOrder), undefined, {
      numeric: true,
      sensitivity: "base",
    });
    if (wo !== 0) return wo;
    return String(a.projectName).localeCompare(String(b.projectName), undefined, {
      sensitivity: "base",
    });
  });
}

export function formatInventoryQuantity(value, unit) {
  if (value == null || value === "") return "—";
  const unitLabel = unit === "Footage" ? "ft" : unit === "Quantity" ? "ea" : "";
  return unitLabel ? `${value} ${unitLabel}` : String(value);
}
